import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { adminApiClient } from "../../services/apiClient";
import { toApiError } from "../../types/api";

import type { RegistrationSettings } from "@lingoshade/domain";

interface RegistrationState {
  settings: RegistrationSettings | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

export const fetchRegistrationSettings = createAsyncThunk<
  RegistrationSettings,
  void,
  { rejectValue: string }
>(
  "registrationSettings/fetch",
  async (
    _,
    { rejectWithValue },
  ): Promise<RegistrationSettings | ReturnType<typeof rejectWithValue>> => {
    try {
      return (
        await adminApiClient.get<RegistrationSettings>(
          "/admin/registration-settings",
        )
      ).data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);

export const saveRegistrationSettings = createAsyncThunk<
  RegistrationSettings,
  RegistrationSettings,
  { rejectValue: string }
>(
  "registrationSettings/save",
  async (
    settings,
    { rejectWithValue },
  ): Promise<RegistrationSettings | ReturnType<typeof rejectWithValue>> => {
    try {
      return (
        await adminApiClient.put<RegistrationSettings>(
          "/admin/registration-settings",
          settings,
        )
      ).data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);

const initialState: RegistrationState = {
  settings: null,
  loading: false,
  saving: false,
  error: null,
};

const registrationSettingsSlice = createSlice({
  name: "registrationSettings",
  initialState,
  reducers: {},
  extraReducers: (builder): void => {
    builder
      .addCase(fetchRegistrationSettings.pending, (state): void => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRegistrationSettings.fulfilled, (state, action): void => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(fetchRegistrationSettings.rejected, (state, action): void => {
        state.loading = false;
        state.error = action.payload ?? action.error.message ?? null;
      })
      .addCase(saveRegistrationSettings.pending, (state): void => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveRegistrationSettings.fulfilled, (state, action): void => {
        state.saving = false;
        state.settings = action.payload;
      })
      .addCase(saveRegistrationSettings.rejected, (state, action): void => {
        state.saving = false;
        state.error = action.payload ?? action.error.message ?? null;
      });
  },
});

export default registrationSettingsSlice.reducer;
